import { useState, useEffect, useCallback } from 'react';
import { scoutSettingsService } from '../services/scoutSettingsService';
import {
  UpdateScoutEmailSettingsRequest,
  ScoutEmailSettings,
} from '../types/scout-settings';
import { useToast } from './useToast';

interface UseScoutEmailSettingsReturn {
  data: ScoutEmailSettings | undefined;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useScoutEmailSettings = (): UseScoutEmailSettingsReturn => {
  const [data, setData] = useState<ScoutEmailSettings | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const settings = await scoutSettingsService.getScoutEmailSettings();
      setData(settings);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'スカウト設定の取得に失敗しました'
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchSettings,
  };
};

interface UseUpdateScoutEmailSettingsReturn {
  mutate: (settings: UpdateScoutEmailSettingsRequest) => Promise<void>;
  isPending: boolean;
}

export const useUpdateScoutEmailSettings =
  (): UseUpdateScoutEmailSettingsReturn => {
    const [isPending, setIsPending] = useState(false);
    const toast = useToast();

    const mutate = useCallback(
      async (settings: UpdateScoutEmailSettingsRequest) => {
        try {
          setIsPending(true);
          const result =
            await scoutSettingsService.updateScoutEmailSettings(settings);

          // 成功通知
          toast.success(result.message);
        } catch (error) {
          // エラー通知
          toast.error(
            error instanceof Error
              ? error.message
              : 'スカウト設定の更新に失敗しました'
          );
          throw error; // エラーを再スローして呼び出し元でキャッチできるようにする
        } finally {
          setIsPending(false);
        }
      },
      [toast]
    );

    return {
      mutate,
      isPending,
    };
  };
